import { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import ListGroup from "react-bootstrap/ListGroup";
import { formatDuration } from "shared/durationFormatter";
import { toCurrency } from "shared/utilities";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator";
import SizePerPage from "member/components/Pagination/SizePerPage";
import Total from "member/components/Pagination/Total";

import cellEditFactory, { Type } from "react-bootstrap-table2-editor";
import ManageColumn from "./ManageColumn";
import _ from "lodash";
import Button from "react-bootstrap/Button";
import { connect } from "react-redux";
import * as searchResultActions from "member/redux/modules/searchResults";
import CommentEditor from "./CommentEditor";
import { Badge } from "react-bootstrap";
import { printReport } from "shared/utilities";
import Spinner from "react-bootstrap/Spinner";
import Container from "react-bootstrap/Container";
import { useFullScreen } from "../../PowerSearchDemo/contexts/FullScreenProvider";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import { POWER_SEARCH_EVENT_NAMES } from "shared/trackEvents/constants";

const { ExportCSVButton } = CSVExport;

// Wrapper component to access fullscreen context
const TableResultWrapper = (props) => {
  const { isFullScreen } = useFullScreen();
  return <TableResult {...props} isFullScreen={isFullScreen} />;
};

class TableResult extends Component {
  constructor(props) {
    super(props);
    this.state = {
      columnsProp: {
        name: { hidden: false, order: 1 },
        institutions: { hidden: false, order: 2 },
        roles: { hidden: false, order: 3 },
        organisation: { hidden: false, order: 4 },
        market_cap: { hidden: false, order: 5 },
        age: { hidden: false, order: 6 },
        time: { hidden: false, order: 7 },
        remunerations: { hidden: true, order: 8 },
        start_dates: { hidden: true, order: 9 },
        end_dates: { hidden: true, order: 10 },
        location: { hidden: true, order: 11 },
        educations: { hidden: true, order: 12 },
        director_skill_and_experiences: { hidden: true, order: 13 },
        comment: { hidden: true, order: 14 },
      },
      checkedRows: [],
      hiddenRows: [],
      comments: {},
      printing: false,
    };
  }

  // For pagination
  _handleTableChange = (type, newState) => {
    const { page, sizePerPage, filters, sortField, sortOrder } = newState;
    const { params } = this.props;
    const workSpaceParams = {
      ...params,
      page,
      sort_by: sortField,
      sort_order: sortOrder,
      per_page: sizePerPage,
    };
    this.props.loadSearchResultData(workSpaceParams, () => {});
  };

  // For comment edit
  _saveCell = (oldValue, newValue, row, column, done) => {
    setTimeout(() => {
      this.setState(
        {
          comments: _.merge(this.state.comments, { [row.id]: newValue }),
        },
        () => {
          done(true);
        }
      );
    }, 0);
    return { async: true };
  };

  _saveColumnsOrder = (columns) => {
    const orderObj = {};
    columns.forEach(
      (column, index) => (orderObj[column] = { order: index + 1 })
    );
    let result = _.merge(this.state.columnsProp, orderObj);
    this.setState({
      columnsProp: result,
    });
  };

  _saveColumnDisplay = (column, status, cb) => {
    cb();
    let result = _.merge(this.state.columnsProp, {
      [column]: { hidden: status },
    });
    this.setState({
      columnsProp: result,
    });
  };

  _ageHeaderFormatter = (column, colIndex) => (
    <span>
      Age{" "}
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip
            id="age-est-tooltip"
            className={this.props.isFullScreen ? "tooltip-fullscreen" : ""}
          >
            Estimated age only
          </Tooltip>
        }
      >
        <i
          className="fa fa-info-circle"
          style={{ color: "#888", cursor: "pointer" }}
        />
      </OverlayTrigger>
      <span style={{ fontSize: "0.8em", color: "#888", marginLeft: 4 }}>
        (EST)
      </span>
    </span>
  );

  _columns = () => {
    const { getMembers } = this.props;
    const { columnsProp, comments } = this.state;
    return [
      {
        dataField: "name",
        text: "Name",
        formatter: (cell, row) => (
          <Link to={`/directors/${row.id}`} target="_blank">
            {cell}
          </Link>
        ),
        editable: false,
        hidden: columnsProp.name.hidden,
        order: columnsProp.name.order,
        sort: true,
        csvExport: !columnsProp.name.hidden,
      },
      {
        dataField: "institutions",
        text: "Institution",
        formatter: (cellContent, row) => {
          return getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>
                <Link to={member.link} target="_blank" className={member.class}>
                  {member.name}
                </Link>
              </ListGroup.Item>
            </ListGroup>
          ));
        },
        editable: false,
        hidden: columnsProp.institutions.hidden,
        order: columnsProp.institutions.order,
        style: (cell, row, rowIndex, colIndex) => {
          return {
            minWidth: "350px",
          };
        },
        sort: true,
        csvExport: !columnsProp.institutions.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => m.name)
            .join(),
      },
      {
        dataField: "roles",
        text: "Role",
        formatter: (cellContent, row) => {
          return getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>{member.seat_name}</ListGroup.Item>
            </ListGroup>
          ));
        },
        sort: true,
        editable: false,
        hidden: columnsProp.roles.hidden,
        order: columnsProp.roles.order,
        style: (cell, row, rowIndex, colIndex) => {
          return {
            minWidth: "300px",
          };
        },
        csvExport: !columnsProp.roles.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => m.seat_name)
            .join(),
      },
      {
        dataField: "organisation",
        text: "Org. Type",
        sort: true,
        formatter: (cellContent, row) =>
          getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>{member.institution_type}</ListGroup.Item>
            </ListGroup>
          )),
        editable: false,
        hidden: columnsProp.organisation.hidden,
        order: columnsProp.organisation.order,
        style: (cell, row, rowIndex, colIndex) => {
          return {
            minWidth: "150px",
          };
        },
        csvExport: !columnsProp.organisation.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => m.institution_type)
            .join(),
      },
      {
        dataField: "market_cap",
        text: "Mkt. Cap",
        sort: true,
        formatter: (cellContent, row) =>
          getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>
                {toCurrency(member.market_capital)}
              </ListGroup.Item>
            </ListGroup>
          )),
        editable: false,
        hidden: columnsProp.market_cap.hidden,
        order: columnsProp.market_cap.order,
        csvExport: !columnsProp.market_cap.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => toCurrency(m.market_capital))
            .join(),
      },
      {
        dataField: "age",
        text: "Age",
        sort: true,
        editable: false,
        hidden: columnsProp.age.hidden,
        order: columnsProp.age.order,
        csvExport: !columnsProp.age.hidden,
        csvType: Number,
        formatter: (cellContent, row) => {
          if (!row?.age_type && row.calculated_age <= 0) {
            return (
              <span
                style={{
                  padding: "12px 4px",
                }}
              >
                N/A
              </span>
            );
          }
          if (
            (!row?.age_type || row.age_type === "estimated_dob") &&
            row.calculated_age > 0
          ) {
            return (
              <OverlayTrigger
                overlay={
                  <Tooltip
                    id="age-est-tooltip"
                    className={
                      this.props.isFullScreen ? "tooltip-fullscreen" : ""
                    }
                  >
                    Estimated age only
                  </Tooltip>
                }
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    padding: "12px 4px",
                    justifyContent: "center",
                  }}
                >
                  {row.calculated_age || cellContent}
                  <i
                    className="fa fa-info-circle"
                    style={{ color: "#AF6A3A", cursor: "pointer" }}
                  />
                </span>
              </OverlayTrigger>
            );
          }
          return (
            <span
              style={{
                padding: "12px 4px",
              }}
            >
              {cellContent}
            </span>
          );
        },
      },
      {
        dataField: "time",
        text: "Time",
        sort: true,
        formatter: (cellContent, row) =>
          getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>
                {formatDuration(member.time_on_board_in_ms)}
              </ListGroup.Item>
            </ListGroup>
          )),
        editable: false,
        hidden: columnsProp.time.hidden,
        order: columnsProp.time.order,
        style: (cell, row, rowIndex, colIndex) => {
          return {
            minWidth: "170px",
          };
        },
        csvExport: !columnsProp.time.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => formatDuration(m.time_on_board_in_ms))
            .join(),
      },
      {
        dataField: "remunerations",
        text: "Remuneration",
        formatter: (cellContent, row) =>
          getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>{toCurrency(member.remuneration)}</ListGroup.Item>
            </ListGroup>
          )),
        hidden: columnsProp.remunerations.hidden,
        order: columnsProp.remunerations.order,
        editable: false,
        sort: true,
        csvExport: !columnsProp.remunerations.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => toCurrency(m.remuneration))
            .join(),
      },
      {
        dataField: "start_dates",
        text: "Start Date",
        formatter: (cellContent, row) =>
          getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>
                {member.start_date && member.start_date}
                {!member.start_date && `N/A`}
              </ListGroup.Item>
            </ListGroup>
          )),
        hidden: columnsProp.start_dates.hidden,
        order: columnsProp.start_dates.order,
        editable: false,
        sort: true,
        csvExport: !columnsProp.start_dates.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => {
              if (m.start_date) return m.start_date;
              return "N/A";
            })
            .join(),
      },
      {
        dataField: "end_dates",
        text: "End Date",
        formatter: (cellContent, row) =>
          getMembers(row).map((member, index) => (
            <ListGroup key={index}>
              <ListGroup.Item>
                {member.end_date && member.end_date}
                {!member.end_date && `N/A`}
              </ListGroup.Item>
            </ListGroup>
          )),
        hidden: columnsProp.end_dates.hidden,
        order: columnsProp.end_dates.order,
        editable: false,
        sort: true,
        csvExport: !columnsProp.end_dates.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          getMembers(row)
            .map((m) => {
              if (m.end_date) return m.end_date;
              return "N/A";
            })
            .join(),
      },
      {
        dataField: "location",
        text: "Location",
        sort: true,
        hidden: columnsProp.location.hidden,
        order: columnsProp.location.order,
        editable: false,
        csvExport: !columnsProp.location.hidden,
        formatter: (cellContent, row) => row.short_address || "",
        csvFormatter: (cell, row) => row.short_address || "",
      },
      {
        dataField: "educations",
        text: "Qualifications",
        formatter: (educations) =>
          educations.map((ed, index) => <p key={index}> {ed.full_name} </p>),
        hidden: columnsProp.educations.hidden,
        order: columnsProp.educations.order,
        editable: false,
        sort: true,
        csvExport: !columnsProp.educations.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          cell.map((ed) => ed.full_name).join(),
      },
      {
        dataField: "director_skill_and_experiences",
        text: "Skills/Experience",
        formatter: (dses) =>
          dses.map((dse, index) => <p key={index}> {dse.name} </p>),
        hidden: columnsProp.director_skill_and_experiences.hidden,
        order: columnsProp.director_skill_and_experiences.order,
        editable: false,
        sort: true,
        csvExport: !columnsProp.director_skill_and_experiences.hidden,
        csvFormatter: (cell, row, rowIndex) =>
          cell.map((dse) => dse.name).join(),
      },
      {
        dataField: "comment",
        text: "Comment",
        editable: true,
        formatter: (cellContent, row) => {
          if (comments[row.id]) {
            return <p className="text-sm-left"> {comments[row.id]}</p>;
          }
          return (
            <Badge
              variant="primary"
              style={{ color: "white", cursor: "pointer", fontSize: "100%" }}
            >
              {" "}
              Click to add
            </Badge>
          );
        },
        editor: {
          type: Type.TEXTAREA,
        },
        editorRenderer: (
          editorProps,
          value,
          row,
          column,
          rowIndex,
          columnIndex
        ) => <CommentEditor {...editorProps} value={value} row={row} />,
        hidden: columnsProp.comment.hidden,
        order: columnsProp.comment.order,
        sort: false,
        csvExport: !columnsProp.comment.hidden,
        csvFormatter: (cell, row, rowIndex) => {
          if (comments[row.id]) return comments[row.id];
          return "";
        },
      },
    ].sort((first, second) => (first.order > second.order ? 1 : -1));
  };

  _checkRow = (row, isSelect) => {
    const { checkedRows } = this.state;
    let finalValue;
    if (isSelect) {
      checkedRows.push(row.id);
      finalValue = checkedRows;
    } else {
      finalValue = checkedRows.filter((item) => item !== row.id);
    }
    this.setState({
      checkedRows: finalValue,
    });
  };

  _checkAllRow = (rows, isSelect) => {
    let finalValue;
    if (isSelect) {
      finalValue = rows.map((row) => row.id);
    } else {
      finalValue = [];
    }
    this.setState({
      checkedRows: finalValue,
    });
  };

  _hideSelection = (hide) => {
    if (hide) {
      this.setState({
        hiddenRows: this.state.checkedRows,
      });
    } else {
      this.setState({
        hiddenRows: [],
      });
    }
  };

  _exportToExcel = (cb) => {
    const { params } = this.props;
    params.full = true;

    if (this.props.all_search_results.length === 0) {
      this.props.loadAllSearchResultData(params).then(() => {
        cb();
      });
    } else {
      cb();
    }
  };

  _startPrint = (cb) => {
    this.setState(
      {
        printing: true,
      },
      () => {
        cb();
      }
    );
  };

  _donePrinting = () => {
    this.setState({
      printing: false,
    });
  };

  _trackDownload = () => {
    const eventName = isOpenRemunerationPlan(this.props.current_user)
      ? POWER_SEARCH_EVENT_NAMES.OR_WORKSPACE_DOWNLOAD_CLICKED
      : POWER_SEARCH_EVENT_NAMES.OR_WORKSPACE_DOWNLOAD_CLICKED;

    mixpanelTrack(eventName, {
      userEmail: this.props.current_user?.email,
    });
  };

  _printReport = () => {
    const allResults =
      (this.props.allResultData && this.props.allResultData.search_results) ||
      [];
    const searchResultsWithLocation = allResults.map((result) => ({
      ...result,
      location: result.location || result.short_address || "",
    }));
    const data = {
      ...this.props.allResultData,
      search_results: searchResultsWithLocation,
      columns: this._columns(),
      hiddenRows: this.state.hiddenRows,
      comments: this.state.comments,
    };

    printReport(
      "dKgjcn0p91",
      "Search Result.xlsx",
      data,
      this._startPrint,
      this._donePrinting,
      this.props.current_user.email
    );
  };

  render() {
    const { data, pageNumber, sizePerPage, totalPages, allLoading, loading } =
      this.props;
    const enrichedData = data.map((row) => ({
      ...row,
      location: row.location || row.short_address || "",
    }));
    const columns = this._columns();

    const sizePerPageNumber = Number(sizePerPage);
    const totalSize = totalPages * sizePerPageNumber;

    const isUnderExportLimit = totalSize / 25 <= 13;

    const paginationOptions = {
      custom: true,
      page: pageNumber,
      totalSize: totalSize,
      sizePerPage: sizePerPageNumber,
      showTotal: true,
    };

    const selectRow = {
      mode: "checkbox",
      clickToSelect: false,
      clickToEdit: true,
      onSelect: (row, isSelect, rowIndex, e) => {
        this._checkRow(row, isSelect);
      },
      onSelectAll: (isSelect, rows, e) => {
        this._checkAllRow(rows, isSelect);
      },
    };

    const hiddenRowKeys = this.state.hiddenRows;

    return (
      <div style={{ maxWidth: "100%" }} className="mb-5">
        <div>
          {data.length > 0 && (
            <ToolkitProvider
              bootstrap4
              keyField="id"
              data={enrichedData}
              columns={columns}
              columnToggle
              exportCSV={{
                fileName: "Search Result.csv",
              }}
            >
              {(props) => (
                <PaginationProvider
                  pagination={paginationFactory(paginationOptions)}
                >
                  {({ paginationProps, paginationTableProps }) => (
                    <div>
                      <b>Manage Columns</b>
                      <ManageColumn
                        {...props.columnToggleProps}
                        handleOrder={this._saveColumnsOrder}
                        handleDisplay={this._saveColumnDisplay}
                      />
                      <hr />
                      {this.state.hiddenRows.length === 0 && (
                        <>
                          {this.state.checkedRows.length === 0 && (
                            <Button
                              variant="secondary"
                              style={{ padding: "2px", fontSize: "0.8rem" }}
                            >
                              Select rows
                            </Button>
                          )}
                          {this.state.checkedRows.length > 0 && (
                            <Button
                              variant="primary"
                              style={{ padding: "2px", fontSize: "0.8rem" }}
                              onClick={(e) => {
                                this._hideSelection(true);
                              }}
                            >
                              Hide {this.state.checkedRows.length} selected rows
                            </Button>
                          )}
                        </>
                      )}
                      {this.state.hiddenRows.length > 0 && (
                        <Button
                          variant="primary"
                          style={{ padding: "2px", fontSize: "0.8rem" }}
                          onClick={(e) => {
                            this._hideSelection(false);
                          }}
                        >
                          Show {this.state.checkedRows.length} hidden rows
                        </Button>
                      )}
                      {allLoading && (
                        <Button
                          variant="secondary"
                          className="ml-2"
                          style={{ padding: "2px", fontSize: "0.8rem" }}
                        >
                          Fetching all results...
                        </Button>
                      )}
                      {!allLoading && (
                        <>
                          {isUnderExportLimit && (
                            <>
                              {this.state.printing && (
                                <Button
                                  variant="secondary"
                                  className="ml-2"
                                  style={{ padding: "2px", fontSize: "0.8rem" }}
                                >
                                  Generating Report...
                                </Button>
                              )}
                              {!this.state.printing && (
                                <Button
                                  variant="primary"
                                  onClick={() => {
                                    this._trackDownload();
                                    this._exportToExcel(this._printReport);
                                  }}
                                  className="ml-2"
                                  style={{ padding: "2px", fontSize: "0.8rem" }}
                                >
                                  Export to Excel
                                </Button>
                              )}
                              <ExportCSVButton
                                {...props.csvProps}
                                className="ml-2 btn btn-primary"
                                style={{ padding: "2px", fontSize: "0.8rem" }}
                              >
                                Export to CSV
                              </ExportCSVButton>
                            </>
                          )}
                          {!isUnderExportLimit && (
                            <Button
                              variant="secondary"
                              className="ml-2"
                              style={{ padding: "2px", fontSize: "0.8rem" }}
                            >
                              Download exceeds 13 pages maximum
                            </Button>
                          )}
                        </>
                      )}
                      {loading && (
                        <div className="float-right">
                          <Spinner animation="grow" variant="success" />
                        </div>
                      )}
                      <hr />

                      <Row>
                        <Col md={1}>
                          <SizePerPage
                            paginationProps={paginationProps}
                            totalSize={totalSize}
                            sizePerPage={sizePerPageNumber}
                          />
                        </Col>
                        <Col md={3}>
                          {loading && (
                            <Spinner animation="grow" variant="success" />
                          )}
                          {!loading && (
                            <div className="pt-2">
                              <Total paginationProps={paginationProps} />
                            </div>
                          )}
                        </Col>
                        <Col md={5} className="offset-md-3">
                          {data.length > 0 && (
                            <PaginationListStandalone {...paginationProps} />
                          )}
                        </Col>
                      </Row>

                      <BootstrapTable
                        remote
                        bordered={false}
                        onTableChange={this._handleTableChange}
                        noDataIndication={() => <p> No results found</p>}
                        selectRow={selectRow}
                        hiddenRows={hiddenRowKeys}
                        cellEdit={cellEditFactory({
                          mode: "click",
                          blurToSave: true,
                          beforeSaveCell: this._saveCell,
                        })}
                        wrapperClasses="flipped"
                        {...paginationTableProps}
                        {...props.baseProps}
                      />

                      <hr />
                      <Row>
                        <Col md={1}>
                          <SizePerPage
                            paginationProps={paginationProps}
                            totalSize={totalSize}
                            sizePerPage={sizePerPageNumber}
                          />
                        </Col>
                        <Col md={3}>
                          {loading && (
                            <Spinner animation="grow" variant="success" />
                          )}
                          {!loading && (
                            <div className="pt-2">
                              <Total paginationProps={paginationProps} />
                            </div>
                          )}
                        </Col>
                        <Col md={5} className="offset-md-3">
                          {data.length > 0 && (
                            <PaginationListStandalone {...paginationProps} />
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                </PaginationProvider>
              )}
            </ToolkitProvider>
          )}
        </div>
      </div>
    );
  }
}

TableResult.propTypes = {
  getMembers: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
};

function mapStateToProps(state, props) {
  const { searchResults, users } = state;

  const {
    result: { search_results, page, per_page, pages },
    all_result: { search_results: all_search_results },
    allLoading,
    loading,
  } = searchResults;

  return {
    data: search_results,
    pageNumber: page,
    sizePerPage: per_page,
    totalPages: pages,
    allResultData: searchResults.all_result,
    all_search_results,
    allLoading,
    loading,
    current_user: users.user,
  };
}

const mapDispatchToProps = {
  loadSearchResultData: searchResultActions.loadSearchResultData,
  loadAllSearchResultData: searchResultActions.loadAllSearchResultData,
};

export default connect(mapStateToProps, mapDispatchToProps)(TableResultWrapper);
